import * as React from 'react';
import { Field } from '@api';
import { ApiDocumentType } from '../api/documentTypes';
import { canCreateInDrawer } from '../api/drawerPermissions';
import { ImportIcClient } from '../api/importIcClient';
import { http } from '../api/http';
import { EditDocumentModal } from './EditDocumentModal';

export interface AddDocumentDropzoneProps {
  drawerId: number | null;
  drawerName: string | null;
  documentFields: Field[];
  documentTypes: ApiDocumentType[];
  defaultFolderId?: number | null;
  onImported?: (doc: any) => void;
  inline?: boolean;
}

function guessDocumentTypeIdFromExtension(fileName: string, documentTypes: ApiDocumentType[]): number | null {
  const m = (fileName || '').toLowerCase().match(/\.([a-z0-9]+)$/);
  const ext = (m?.[1] || '').trim();
  if (!ext) return null;

  const normalizeDocTypeExt = (extRaw: any): string[] => {
    const s = (extRaw || '').toString().toLowerCase().trim();
    if (!s) return [];
    return s
      .split(/[;,\s]+/g)
      .map(x => x.trim())
      .filter(Boolean)
      .map(x => (x.startsWith('.') ? x.slice(1) : x));
  };

  const hit = (documentTypes || []).find(dt => {
    const list = normalizeDocTypeExt(dt.documentTypeExtension || dt.DocumentTypeExtension);
    return list.includes(ext);
  });
  return hit ? hit.id : null;
}

function fileNameWithoutExtension(name: string): string {
  const s = (name || '').toString();
  const i = s.lastIndexOf('.');
  if (i <= 0) return s;
  return s.slice(0, i);
}

export function AddDocumentDropzone({ drawerId, drawerName, documentFields, documentTypes, defaultFolderId, onImported, inline = false }: AddDocumentDropzoneProps) {
  const [canCreate, setCanCreate] = React.useState(false);
  const [permError, setPermError] = React.useState<string | null>(null);

  const [dragDepth, setDragDepth] = React.useState(0);

  const [file, setFile] = React.useState<File | null>(null);
  const [values, setValues] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!drawerId) { setCanCreate(false); setPermError(null); return; }
    let cancelled = false;
    setPermError(null);
    (async () => {
      try {
        const ok = await canCreateInDrawer(drawerId);
        if (!cancelled) setCanCreate(ok);
      } catch (e: any) {
        if (cancelled) return;
        setCanCreate(false);
        setPermError(e?.message || 'Failed to load permissions');
      }
    })();
    return () => { cancelled = true; };
  }, [drawerId]);

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  function startForFile(f: File) {
    setFile(f);
    setError(null);

    const guessedTypeId = guessDocumentTypeIdFromExtension(f.name, documentTypes);
    const hasExt = /\.[a-z0-9]+$/i.test((f.name || '').toString());
    setValues({
      description: fileNameWithoutExtension(f.name),
      documentTypeId: guessedTypeId ?? (hasExt ? '' : (documentTypes?.[0]?.id ?? '')),
      folderId: defaultFolderId ?? null,
      subfoldername: ''
    });
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragDepth(0);
    const f = e.dataTransfer?.files?.[0];
    if (f) startForFile(f);
  }

  async function onSave() {
    if (!drawerId) return;
    if (!drawerName) { setError('Drawer name is missing'); return; }
    if (!file) return;

    if (!values.folderId) { setError('Folder is required'); return; }

    const dtId = Number(values.documentTypeId);
    const dt = (documentTypes || []).find(d => Number(d.id) === dtId);
    const doctype = dt?.documentTypeName || dt?.DocumentTypeName;
    if (!doctype) { setError('No document type available for the type of file you are archiving.'); return; }

    setSaving(true);
    setError(null);
    try {
      const { description, documentTypeId, folderId, subfoldername, ...rest } = values;
      const docId = await ImportIcClient.importFile({
        drawerName,
        file,
        doctype,
        documentDescription: (description || '').toString().trim() || fileNameWithoutExtension(file.name),
        folderId: Number(folderId),
        subfoldername: (subfoldername || '').toString().trim() || null,
        fieldValues: rest
      });

      // Fetch full doc dto so caller can refresh grid if desired
      try {
        const doc = await http<any>(`/api/document/${docId}`);
        onImported?.(doc);
      } catch {
        // ignore
      }

      setFile(null);
      setValues({});
    } catch (e: any) {
      setError(e?.message || 'Failed to import document');
    } finally {
      setSaving(false);
    }
  }

  function onCancel() {
    setFile(null);
    setValues({});
    setError(null);
    setSaving(false);
  }

  if (!drawerId) return null;

  // Keep the intended UX: only show the dropzone when allowed.
  // But if permission lookup fails (e.g. backend endpoint not deployed yet), show a visible error
  // so the user isn't left wondering why the dropzone is missing.
  if (!canCreate && !permError) return null;

  const isDragActive = dragDepth > 0;

  const baseContainerStyle: React.CSSProperties = inline
    ? {
        border: '1px dashed #e0e6ed',
        background: '#f7fafd',
        borderRadius: 6,
        padding: '4px 8px',
        cursor: canCreate ? 'pointer' : 'default',
        opacity: canCreate ? 1 : 0.95,
        display: 'flex',
        alignItems: permError ? 'flex-start' : 'center',
        flexDirection: permError ? 'column' : 'row',
        gap: 8,
        whiteSpace: permError ? 'normal' : 'nowrap',
      }
    : {
        border: '1px dashed #e0e6ed',
        background: '#f7fafd',
        borderRadius: 6,
        padding: 6,
        marginBottom: 6,
        cursor: canCreate ? 'pointer' : 'default',
        opacity: canCreate ? 1 : 0.95,
      };

  const containerStyle: React.CSSProperties = !canCreate
    ? baseContainerStyle
    : {
        ...baseContainerStyle,
        border: isDragActive ? '2px dashed #49B280' : baseContainerStyle.border,
        background: isDragActive ? '#E4F3EC' : (baseContainerStyle as any).background,
      };

  return (
    <>
      <div
        onDragEnter={e => {
          if (!canCreate) return;
          e.preventDefault();
          e.stopPropagation();
          setDragDepth(d => d + 1);
        }}
        onDragOver={e => {
          if (!canCreate) return;
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragLeave={e => {
          if (!canCreate) return;
          e.preventDefault();
          e.stopPropagation();
          setDragDepth(d => Math.max(0, d - 1));
        }}
        onDrop={e => { if (canCreate) onDrop(e); }}
        onClick={() => { if (canCreate) openFilePicker(); }}
        style={containerStyle}
        title="Drop a file here or click to select"
      >
        <div style={{ fontWeight: 600, color: '#2d3e50' }}>Add document</div>
        <div style={{ fontSize: 12, color: '#556' }}>
          {canCreate ? (isDragActive ? 'Drop to upload' : 'Drop or click') : 'Upload unavailable'}
        </div>
        {permError && (
          <div style={{ fontSize: 12, color: 'red', marginTop: 6 }}>
            Permission check failed: {permError}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={e => {
          const f = (e.target as HTMLInputElement).files?.[0];
          if (f) startForFile(f);
          // allow re-selecting same file
          (e.target as HTMLInputElement).value = '';
        }}
      />

      {file && (
        <EditDocumentModal
          doc={{}}
          mode="create"
          fileName={file.name}
          drawerId={drawerId}
          defaultFolderId={defaultFolderId ?? null}
          documentFields={documentFields}
          documentTypes={documentTypes}
          values={values}
          onChange={setValues}
          onCancel={onCancel}
          onSave={onSave}
          saving={saving}
          error={error}
        />
      )}
    </>
  );
}
